import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowDownRight,
    ArrowUpRight,
    Banknote,
    CalendarClock,
    ClipboardList,
    Landmark,
    Receipt,
    TrendingUp,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatBRL } from "@/lib/utils";
import { financeService } from "@/services/finance.service";
import {
    FINANCE_STATUS_LABELS,
    FinanceStatusBadge,
} from "@/features/finance/components/FinanceStatusBadge";

function KpiCard({ testid, icon: Icon, label, value, sub, tone = "neutral" }) {
    const toneClass = {
        neutral: "bg-muted/40 text-foreground",
        positive: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
        negative: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300",
        warning: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
    }[tone];
    return (
        <Card
            data-testid={testid}
            className="transition-shadow transition-colors duration-150 hover:shadow-sm hover:border-border/80"
        >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                    {label}
                </CardTitle>
                <div className={`grid h-9 w-9 place-items-center rounded-md ${toneClass}`}>
                    <Icon className="h-4 w-4" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-semibold tracking-tight sm:text-3xl">
                    {value}
                </div>
                {sub && (
                    <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
                )}
            </CardContent>
        </Card>
    );
}

export default function FinanceDashboardPage() {
    const navigate = useNavigate();
    const dashboard = useQuery({
        queryKey: ["finance-dashboard"],
        queryFn: () => financeService.dashboard(),
        refetchInterval: 60_000,
    });

    const kpis = dashboard.data?.kpis || {};
    const receivableByStatus = dashboard.data?.receivable_by_status || {};
    const payableByStatus = dashboard.data?.payable_by_status || {};

    return (
        <div className="space-y-6" data-testid="finance-dashboard">
            <PageHeader
                title="Financeiro"
                description="Visão geral de contas a receber, contas a pagar e movimentações do mês."
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate("/finance/receivables")}
                            data-testid="finance-dashboard-goto-receivables"
                        >
                            <ArrowDownRight className="mr-2 h-4 w-4" />
                            A Receber
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate("/finance/payables")}
                            data-testid="finance-dashboard-goto-payables"
                        >
                            <ArrowUpRight className="mr-2 h-4 w-4" />
                            A Pagar
                        </Button>
                    </div>
                }
            />

            {dashboard.isLoading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={`kpi-skeleton-${i}`} className="h-32 w-full rounded-lg" />
                    ))}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <KpiCard
                            testid="finance-kpi-total-receivable"
                            icon={Landmark}
                            label="Total a receber (saldo)"
                            value={formatBRL(kpis.total_receivable || 0)}
                            sub={`Recebido no mês: ${formatBRL(kpis.received_this_month || 0)}`}
                            tone="positive"
                        />
                        <KpiCard
                            testid="finance-kpi-total-payable"
                            icon={Receipt}
                            label="Total a pagar (saldo)"
                            value={formatBRL(kpis.total_payable || 0)}
                            sub={`Pago no mês: ${formatBRL(kpis.paid_this_month || 0)}`}
                            tone="warning"
                        />
                        <KpiCard
                            testid="finance-kpi-overdue-receivable"
                            icon={ClipboardList}
                            label="Recebíveis vencidos"
                            value={formatBRL(kpis.overdue_receivable_amount || 0)}
                            sub={`${kpis.overdue_receivable_count || 0} parcela(s) em atraso`}
                            tone={kpis.overdue_receivable_amount > 0 ? "negative" : "neutral"}
                        />
                        <KpiCard
                            testid="finance-kpi-overdue-payable"
                            icon={ClipboardList}
                            label="Pagáveis vencidos"
                            value={formatBRL(kpis.overdue_payable_amount || 0)}
                            sub={`${kpis.overdue_payable_count || 0} parcela(s) em atraso`}
                            tone={kpis.overdue_payable_amount > 0 ? "negative" : "neutral"}
                        />
                        <KpiCard
                            testid="finance-kpi-next30-receivable"
                            icon={CalendarClock}
                            label="A receber (próx. 30d)"
                            value={formatBRL(kpis.next30_receivable || 0)}
                            tone="neutral"
                        />
                        <KpiCard
                            testid="finance-kpi-next30-payable"
                            icon={CalendarClock}
                            label="A pagar (próx. 30d)"
                            value={formatBRL(kpis.next30_payable || 0)}
                            tone="neutral"
                        />
                        <KpiCard
                            testid="finance-kpi-received-month"
                            icon={TrendingUp}
                            label="Recebido no mês"
                            value={formatBRL(kpis.received_this_month || 0)}
                            tone="positive"
                        />
                        <KpiCard
                            testid="finance-kpi-paid-month"
                            icon={Banknote}
                            label="Pago no mês"
                            value={formatBRL(kpis.paid_this_month || 0)}
                            tone="warning"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <Card data-testid="finance-chart-receivable-status">
                            <CardHeader>
                                <CardTitle>Distribuição — A Receber</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {Object.keys(receivableByStatus).length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        Sem lançamentos no período selecionado.
                                    </p>
                                ) : (
                                    <StatusRows dist={receivableByStatus} />
                                )}
                            </CardContent>
                        </Card>
                        <Card data-testid="finance-chart-payable-status">
                            <CardHeader>
                                <CardTitle>Distribuição — A Pagar</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {Object.keys(payableByStatus).length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        Sem lançamentos no período selecionado.
                                    </p>
                                ) : (
                                    <StatusRows dist={payableByStatus} />
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}

function StatusRows({ dist }) {
    const total = Object.values(dist).reduce((acc, r) => acc + (r.amount || 0), 0) || 1;
    const entries = Object.entries(dist).sort(
        (a, b) => (b[1].amount || 0) - (a[1].amount || 0),
    );
    return (
        <div className="space-y-3">
            {entries.map(([status, row]) => {
                const pct = ((row.amount || 0) / total) * 100;
                return (
                    <div key={status} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <FinanceStatusBadge status={status} />
                                <span className="text-muted-foreground">
                                    {row.count} conta(s)
                                </span>
                            </div>
                            <span className="font-medium">{formatBRL(row.amount || 0)}</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                            <div
                                className={`h-full rounded-full ${statusBarColor(status)}`}
                                style={{ width: `${Math.min(100, pct).toFixed(1)}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function statusBarColor(status) {
    return {
        em_aberto: "bg-slate-400",
        parcial: "bg-amber-400",
        paga: "bg-emerald-500",
        vencida: "bg-red-500",
        cancelada: "bg-muted-foreground/50",
    }[status] || "bg-slate-400";
}
